import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import * as announcementsApi from 'services/announcementsApi';
import { Announcement, AnnouncementDetailState } from 'types/announcements';

const initialState: AnnouncementDetailState = {
    details: null,
    loading: false,
    error: false,
    inflightParishId: null,
    inflightAnnouncementId: null,
};

export const fetchAnnouncementDetails = createAsyncThunkWrapper<
    { details: Announcement; parishId: string; announcementId: string } | null,
    { parishId: string; id: string }
>(
    'AnnouncementDetails/fetchAnnouncementDetails',
    async ({ parishId, id }, { getState }) => {
        if (!parishId || !id) return null;
        try {
            const details = await announcementsApi.getAnnouncement(parishId, id);
            if (parishSelectionMoved(getState, parishId)) return null;
            return { details, parishId, announcementId: id };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load announcement' }
);

const announcementDetailsSlice = createSlice({
    name: 'announcementDetails',
    initialState,
    reducers: {
        clearAnnouncementDetails(state) {
            state.details = null;
            state.inflightParishId = null;
            state.inflightAnnouncementId = null;
            state.error = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAnnouncementDetails.pending, (state, action) => {
            state.loading = true;
            state.error = false;
            state.details = null;
            state.inflightParishId = action.meta.arg.parishId;
            state.inflightAnnouncementId = action.meta.arg.id;
        });
        builder.addCase(fetchAnnouncementDetails.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightParishId === action.meta.arg.parishId && state.inflightAnnouncementId === action.meta.arg.id) {
                    state.loading = false;
                }
                return;
            }
            const { details, parishId, announcementId } = action.payload;
            if (parishId !== state.inflightParishId || announcementId !== state.inflightAnnouncementId) {
                return;
            }
            state.loading = false;
            state.details = details;
        });
        builder.addCase(fetchAnnouncementDetails.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId && state.inflightAnnouncementId === action.meta.arg.id) {
                state.loading = false;
                state.error = true;
            }
        });
    },
});

export const { clearAnnouncementDetails } = announcementDetailsSlice.actions;
export default announcementDetailsSlice.reducer;
